import React from "react";
import axios from "axios";
import {
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Typography,
  Paper,
  Link,
  Stack,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Email,
  Phone,
  LocationOn,
  Send,
  CheckCircleOutline,
} from "@mui/icons-material";
import HeaderBar from "../widgets/HeaderBar";
import Footer from "../widgets/Footer";
import LayoutContainer from "../widgets/LayoutContainer";

const ContactUs = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      subject: "",
      message: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      subject: Yup.string().required("Subject is required"),
      message: Yup.string().required("Message is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        await axios.post("/api/contact", values);
        alert("Message sent successfully!");
        resetForm();
      } catch (error) {
        alert("Failed to send message. Please try again.");
      }
    },
  });

  return (
    <LayoutContainer>
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          align="center"
          gutterBottom
          sx={{
            fontWeight: 700,
            color: theme.palette.primary.main,
            mb: 6,
            [theme.breakpoints.down("sm")]: { fontSize: "2rem" },
          }}
        >
          Get in Touch
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} md={7}>
            <Paper
              elevation={4}
              sx={{
                p: 4,
                borderRadius: 4,
                background: "linear-gradient(145deg, #ffffff, #f8f9fa)",
              }}
            >
              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Your Name"
                      name="name"
                      variant="outlined"
                      InputProps={{
                        style: { borderRadius: 12 },
                      }}
                      {...formik.getFieldProps("name")}
                      error={formik.touched.name && !!formik.errors.name}
                      helperText={formik.touched.name && formik.errors.name}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      name="email"
                      type="email"
                      InputProps={{
                        style: { borderRadius: 12 },
                      }}
                      {...formik.getFieldProps("email")}
                      error={formik.touched.email && !!formik.errors.email}
                      helperText={formik.touched.email && formik.errors.email}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Subject"
                      name="subject"
                      InputProps={{
                        style: { borderRadius: 12 },
                      }}
                      {...formik.getFieldProps("subject")}
                      error={formik.touched.subject && !!formik.errors.subject}
                      helperText={
                        formik.touched.subject && formik.errors.subject
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Your Message"
                      name="message"
                      multiline
                      rows={6}
                      InputProps={{
                        style: { borderRadius: 16 },
                      }}
                      {...formik.getFieldProps("message")}
                      error={formik.touched.message && !!formik.errors.message}
                      helperText={
                        formik.touched.message && formik.errors.message
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      fullWidth
                      sx={{
                        py: 1.5,
                        borderRadius: 12,
                        fontSize: "1.1rem",
                        textTransform: "none",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                        },
                      }}
                      startIcon={<Send />}
                    >
                      Send Message
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Paper>
          </Grid>

          <Grid item xs={12} md={5}>
            <Box
              sx={{
                p: 4,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                backgroundColor: theme.palette.primary.light,
                borderRadius: 4,
                color: "white",
              }}
            >
              <Typography
                variant="h5"
                gutterBottom
                sx={{ fontWeight: 600, mb: 3 }}
              >
                Contact Information
              </Typography>

              <Stack spacing={3}>
                <Box>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Email sx={{ fontSize: 30 }} />
                    <Link
                      href="mailto:info@betazen.in"
                      color="inherit"
                      underline="hover"
                    >
                      info@betazen.in
                    </Link>
                  </Stack>
                </Box>

                <Box>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Phone sx={{ fontSize: 30 }} />
                    <Link
                      href="tel:+916366228098"
                      color="inherit"
                      underline="hover"
                    >
                      +91 6366-228098
                    </Link>
                  </Stack>
                </Box>

                <Box>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Phone sx={{ fontSize: 30 }} />
                    <Link
                      href="tel:09380792952"
                      color="inherit"
                      underline="hover"
                    >
                      093807 92952
                    </Link>
                  </Stack>
                </Box>

                <Box>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <LocationOn sx={{ fontSize: 30, mt: 0.5 }} />
                    <div>
                      <Typography>56, 1st Floor, G Block,</Typography>
                      <Typography>Near HDFC Bank, Sahakar Nagar,</Typography>
                      <Typography>Byatarayanapura,</Typography>
                      <Typography>Bengaluru, Karnataka 560092</Typography>
                    </div>
                  </Stack>
                </Box>
              </Stack>

              {!isMobile && (
                <>
                  <Divider
                    sx={{ my: 4, borderColor: "rgba(255,255,255,0.2)" }}
                  />
                  <Typography variant="body1" sx={{ fontStyle: "italic" }}>
                    We typically respond within 24 hours
                  </Typography>
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </LayoutContainer>
  );
};

export default ContactUs;
